import dayjs from 'dayjs';

import {
  Box,
  Card,
  Icons,
  ProgressBar,
  Stack,
  Tooltip,
  Typography,
  tokens,
} from '@cast/design-system';

export type CommitmentData = {
  used: number;
  committed: number;
  periodFrom: string;
  periodTo: string;
};

type CommitmentCardProps = {
  commitment: CommitmentData;
};

const formatPeriod = (from: string, to: string): string => {
  const fmt = (d: string) => dayjs(d).format('MMM D, YYYY');
  return `${fmt(from)} — ${fmt(to)}`;
};

export const CommitmentCard = ({ commitment }: CommitmentCardProps) => {
  const { used, committed, periodFrom, periodTo } = commitment;

  const progressValue = committed > 0 ? (used / committed) * 100 : 0;
  const percentUsed = committed > 0 ? Math.round((used / committed) * 100) : 0;

  return (
    <Card
      sx={{
        flex: '1 0 0',
        p: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
      }}
      data-testid="dbo-commitment-card"
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" gap={4}>
        <Typography variant="L10R" color={tokens.theme.fg.structural.secondary}>
          Commitment
        </Typography>
        <Tooltip title="What does commitment mean?">
          <Box component="span" sx={{ display: 'flex' }}>
            <Icons.Info size={14} />
          </Box>
        </Tooltip>
      </Stack>

      <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
        {formatPeriod(periodFrom, periodTo)}
      </Typography>

      {/* Body */}
      <Stack gap={4}>
        <Stack direction="row" alignItems="baseline" gap={4}>
          <Typography color={tokens.theme.fg.structural.primary} variant="P12B">
            {used} / {committed}
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            committed instances is being used
          </Typography>
        </Stack>

        <ProgressBar size="lg" variant="determinate" value={progressValue} />

        <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
          {percentUsed}% used
        </Typography>
      </Stack>
    </Card>
  );
};
